import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { MapPin, Loader2, DollarSign } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { loadGoogleMapsScript, googleMapsFailed, onGoogleMapsFailure } from '@/lib/googleMapsLoader';
import OpenStreetMapFallback from '@/components/OpenStreetMapFallback';
import { getGrade, gradeColor } from '@/lib/grades';

/** Country centroids for the World toggle. Keyed by lowercase country name with
 *  ISO-2 fallback — international_procedure_prices rows carry both. */
const COUNTRY_CENTROIDS: Record<string, [number, number]> = {
  'united states': [39.8, -98.6], us: [39.8, -98.6], usa: [39.8, -98.6],
  canada: [56.1, -106.3], ca: [56.1, -106.3], mexico: [23.6, -102.5], mx: [23.6, -102.5],
  brazil: [-14.2, -51.9], br: [-14.2, -51.9], colombia: [4.6, -74.1], co: [4.6, -74.1],
  'united kingdom': [54.0, -2.5], gb: [54.0, -2.5], uk: [54.0, -2.5],
  france: [46.6, 2.2], fr: [46.6, 2.2], germany: [51.2, 10.4], de: [51.2, 10.4],
  italy: [42.8, 12.8], it: [42.8, 12.8], spain: [40.3, -3.7], es: [40.3, -3.7],
  portugal: [39.6, -8.0], pt: [39.6, -8.0], netherlands: [52.2, 5.3], nl: [52.2, 5.3],
  belgium: [50.6, 4.7], be: [50.6, 4.7], switzerland: [46.8, 8.2], ch: [46.8, 8.2],
  austria: [47.6, 14.1], at: [47.6, 14.1], denmark: [56.1, 9.5], dk: [56.1, 9.5],
  sweden: [62.2, 17.6], se: [62.2, 17.6], norway: [64.5, 11.0], no: [64.5, 11.0],
  poland: [52.1, 19.4], pl: [52.1, 19.4], turkey: [39.0, 35.2], tr: [39.0, 35.2],
  israel: [31.4, 35.0], il: [31.4, 35.0], 'saudi arabia': [24.2, 45.1], sa: [24.2, 45.1],
  iran: [32.4, 53.7], ir: [32.4, 53.7], india: [21.0, 78.0], 'in': [21.0, 78.0],
  thailand: [15.0, 101.0], th: [15.0, 101.0], japan: [36.2, 138.3], jp: [36.2, 138.3],
  'south korea': [36.5, 127.9], kr: [36.5, 127.9], taiwan: [23.7, 121.0], tw: [23.7, 121.0],
  australia: [-25.3, 133.8], au: [-25.3, 133.8], 'new zealand': [-41.5, 172.8], nz: [-41.5, 172.8],
};
const countryLatLng = (name: string, code?: string): [number, number] | null =>
  COUNTRY_CENTROIDS[(name || '').toLowerCase()] || COUNTRY_CENTROIDS[(code || '').toLowerCase()] || null;

interface HospitalMapData {
  hospital_id: string;
  hospital_name: string;
  latitude: number;
  longitude: number;
  city: string;
  state: string;
  gross_charge: number;
  inpatient_outpatient?: string;
  rank?: number;
}

/** Third-party quality signals, keyed by hospital_id. Sources are CMS Care
 *  Compare and Google — none of it is our own rating, which the UI says out
 *  loud so a star next to a price is never read as an endorsement. */
export interface HospitalQuality {
  cms_stars?: number;
  hcahps_recommend?: number;
  google_rating?: number;
  google_reviews?: number;
}

export interface IntlPriceEntry {
  country: string; countryCode?: string; priceUSD: number; flag?: string;
}

interface HospitalPricingMapProps {
  hospitals: HospitalMapData[];
  procedureCode: string;
  codeType: string;
  avgPrice?: number;
  quality?: Map<string, HospitalQuality>;
  /** Per-country USD prices for this code — enables the World toggle. */
  intl?: IntlPriceEntry[];
}

// --- Helper functions (outside component) ---

// A–F grade coloring — the same avg-baseline scale and palette /map/ uses, so a
// grade means one thing everywhere on the site.
function getMarkerColor(grossCharge: number, avgPrice: number): string {
  const percentDiff = avgPrice > 0
    ? ((grossCharge - avgPrice) / avgPrice) * 100
    : 0;
  return gradeColor(getGrade(percentDiff));
}

function buildInfoContent(
  hospital: HospitalMapData,
  color: string,
  avgPrice: number,
  codeType: string,
  procedureCode: string,
  totalCount: number
): string {
  const percentDiff = avgPrice > 0
    ? ((hospital.gross_charge - avgPrice) / avgPrice) * 100
    : 0;
  return `
    <div style="padding: 12px; max-width: 300px; color: #000;">
      <h3 style="margin: 0 0 8px 0; font-size: 16px; font-weight: 600; color: #1f2937;">
        ${hospital.hospital_name}
      </h3>
      <div style="font-size: 14px; color: #6b7280; margin-bottom: 8px;">
        ${hospital.city}, ${hospital.state}
      </div>
      <div style="margin: 8px 0; padding: 8px; background: #f3f4f6; border-radius: 4px;">
        <div style="font-size: 12px; color: #6b7280; margin-bottom: 4px;">
          ${codeType} ${procedureCode} - ${hospital.inpatient_outpatient || 'All Settings'}
        </div>
        <div style="font-size: 20px; font-weight: 700; color: ${color};">
          $${hospital.gross_charge.toLocaleString()}
        </div>
        ${avgPrice > 0 ? `
          <div style="font-size: 12px; margin-top: 4px; color: ${percentDiff > 0 ? '#dc2626' : '#22c55e'};">
            ${percentDiff > 0 ? '+' : ''}${percentDiff.toFixed(1)}% vs avg ($${avgPrice.toLocaleString()})
          </div>
        ` : ''}
      </div>
      ${hospital.rank ? `
        <div style="font-size: 12px; color: #6b7280; margin-top: 8px;">
          Rank: #${hospital.rank} of ${totalCount}
        </div>
      ` : ''}
    </div>
  `;
}

// Google Maps script loading is now in @/lib/googleMapsLoader

export const HospitalPricingMap = React.memo(function HospitalPricingMap({
  hospitals: hospitalsProp,
  procedureCode,
  codeType,
  avgPrice = 0,
  quality,
  intl
}: HospitalPricingMapProps) {
  const navigate = useNavigate();
  // USA = real hospital markers; World = one marker per country (this code's
  // national price in USD), graded on the same A–F scale against the US avg.
  const [scope, setScope] = useState<'usa' | 'world'>('usa');
  // Zoom-aware marker budget: when the viewport changes, respend maxMarkers on
  // hospitals inside it instead of the global top-N (which starves zoomed views).
  const [viewBounds, setViewBounds] = useState<{ n: number; s: number; e: number; w: number } | null>(null);
  // Near-me: geolocated center + radius filter (schoolhouse chip UI below).
  const [userLoc, setUserLoc] = useState<[number, number] | null>(null);
  const [radiusMi, setRadiusMi] = useState<number>(50);
  const [geoErr, setGeoErr] = useState<string | null>(null);
  const milesBetween = (a: [number, number], b: [number, number]) => {
    const R = 3958.8, dLat = (b[0]-a[0])*Math.PI/180, dLon = (b[1]-a[1])*Math.PI/180;
    const q = Math.sin(dLat/2)**2 + Math.cos(a[0]*Math.PI/180)*Math.cos(b[0]*Math.PI/180)*Math.sin(dLon/2)**2;
    return 2*R*Math.asin(Math.sqrt(q));
  };
  const worldMarkers = useMemo<HospitalMapData[]>(() => {
    if (!intl?.length) return [];
    const out: HospitalMapData[] = [];
    if (avgPrice > 0) {
      out.push({ hospital_id: 'country:US', hospital_name: '🇺🇸 United States — national avg',
        latitude: 39.8, longitude: -98.6, city: 'national avg', state: 'US',
        gross_charge: Math.round(avgPrice) });
    }
    for (const c of intl) {
      const ll = countryLatLng(c.country, c.countryCode);
      if (!ll || !(c.priceUSD > 0)) continue;
      out.push({ hospital_id: `country:${c.countryCode || c.country}`,
        hospital_name: `${c.flag ? c.flag + ' ' : ''}${c.country}`,
        latitude: ll[0], longitude: ll[1], city: 'national price (USD)', state: c.countryCode || '',
        gross_charge: Math.round(c.priceUSD) });
    }
    return out;
  }, [intl, avgPrice]);
  const hospitals = scope === 'world' ? worldMarkers : hospitalsProp;
  const brand = useBrand();
  const { isDark } = useTheme();
  const ahpDark = brand.key === 'ahp' && isDark;
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedHospital, setSelectedHospital] = useState<string | null>(null);
  // Flip to the open-source (Leaflet/OSM) fallback if Google Maps auth fails at
  // render time (bad key / referrer not allowed / billing).
  const [gmFailed, setGmFailed] = useState(googleMapsFailed());
  useEffect(() => onGoogleMapsFailure(() => setGmFailed(true)), []);
  const heatmapRef = useRef<google.maps.visualization.HeatmapLayer | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const markerMapRef = useRef<Map<string, google.maps.Marker>>(new Map());
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const rankingsParentRef = useRef<HTMLDivElement>(null);

  // Controls state
  const [maxMarkers, setMaxMarkers] = useState(200);
  const [showExpensive, setShowExpensive] = useState(true);

  // Google Maps API key
  const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

  // Define gradient colors based on brand
  const heatmapGradient = brand.key === 'antigreed'
    ? [
        'rgba(245, 230, 211, 0)',
        'rgba(245, 230, 211, 0.7)',
        'rgba(144, 238, 144, 0.8)',
        'rgba(34, 139, 34, 0.9)',
        'rgba(0, 64, 0, 1)'
      ]
    : brand.key === 'ahp'
    ? [
        'rgba(var(--civic-navy-rgb), 0)',
        'rgba(var(--civic-navy-rgb), 0.5)',
        'rgba(var(--civic-gold-rgb), 0.7)',
        'rgba(var(--civic-red-rgb), 0.85)',
        'rgba(120, 20, 35, 1)'
      ]
    : [
        'rgba(0, 255, 0, 0)',
        'rgba(0, 255, 0, 0.7)',
        'rgba(255, 255, 0, 0.8)',
        'rgba(255, 165, 0, 0.9)',
        'rgba(255, 0, 0, 1)'
      ];

  // Define legend gradient for visual consistency
  const legendGradient = brand.key === 'antigreed'
    ? 'linear-gradient(to right, rgba(255,165,0,0.7), rgba(255,215,0,0.85), rgba(255,100,0,0.95), rgba(255,0,0,1))'
    : brand.key === 'ahp'
    ? 'linear-gradient(to right, rgba(var(--civic-navy-rgb),0.7), rgba(var(--civic-gold-rgb),0.8), rgba(var(--civic-red-rgb),0.9), rgba(120,20,35,1))'
    : 'linear-gradient(to right, rgba(0,255,0,0.7), rgba(255,255,0,0.8), rgba(255,165,0,0.9), rgba(255,0,0,1))';

  // --- Computed lists ---

  const sortedHospitals = useMemo(() => {
    const sorted = [...hospitals];
    sorted.sort((a, b) =>
      showExpensive ? b.gross_charge - a.gross_charge : a.gross_charge - b.gross_charge
    );
    return sorted;
  }, [hospitals, showExpensive]);

  const validHospitals = useMemo(() =>
    hospitals.filter(h =>
      h.latitude && h.longitude &&
      !isNaN(h.latitude) && !isNaN(h.longitude) &&
      h.latitude !== 0 && h.longitude !== 0
    ), [hospitals]);

  const geoFilteredHospitals = useMemo(() => {
    if (!userLoc || scope === 'world') return validHospitals;
    return validHospitals.filter(h => milesBetween(userLoc, [h.latitude, h.longitude]) <= radiusMi);
  }, [validHospitals, userLoc, radiusMi, scope]);

  const visibleMarkerHospitals = useMemo(() => {
    let pool = geoFilteredHospitals;
    if (viewBounds && scope === 'usa' && !userLoc) {
      const inView = pool.filter(h =>
        h.latitude <= viewBounds.n && h.latitude >= viewBounds.s &&
        h.longitude <= viewBounds.e && h.longitude >= viewBounds.w);
      // Only respend the budget when the viewport meaningfully constrains the
      // set — at national zoom inView≈pool and this is a no-op.
      if (inView.length > 0 && inView.length < pool.length * 0.9) pool = inView;
    }
    const sorted = [...pool];
    sorted.sort((a, b) =>
      showExpensive ? b.gross_charge - a.gross_charge : a.gross_charge - b.gross_charge
    );
    return sorted.slice(0, maxMarkers);
  }, [geoFilteredHospitals, viewBounds, scope, userLoc, maxMarkers, showExpensive]);

  // Set of hospital IDs that have markers on the map (for styling)
  const markerHospitalIds = useMemo(() =>
    new Set(visibleMarkerHospitals.map(h => h.hospital_id)),
    [visibleMarkerHospitals]
  );

  // --- Effect 1: Map + Heatmap (runs once when hospitals change) ---
  useEffect(() => {
    const initializeMap = async () => {
      if (!mapRef.current || hospitals.length === 0 || !GOOGLE_MAPS_API_KEY) {
        setLoading(false);
        if (!GOOGLE_MAPS_API_KEY) {
          setError('Google Maps API key not configured');
        }
        return;
      }

      try {
        setLoading(true);
        await loadGoogleMapsScript(GOOGLE_MAPS_API_KEY);

        if (validHospitals.length === 0) {
          setError('No hospitals with valid location data found');
          setLoading(false);
          return;
        }

        // Build bounds from CONUS hospitals only (lat 24–49, lng -125 to -66).
        // Alaska/Hawaii hospitals still render as pins but don't pull the initial
        // framing off-center — without this filter, a single AK pin shifted the
        // continental US to the right edge of the viewport.
        const isConus = (lat: number, lng: number) =>
          lat >= 24 && lat <= 49 && lng >= -125 && lng <= -66;

        const conusHospitals = validHospitals.filter(h => isConus(h.latitude, h.longitude));
        const bounds = new google.maps.LatLngBounds();
        const boundsSource = conusHospitals.length > 0 ? conusHospitals : validHospitals;
        boundsSource.forEach(hospital => {
          bounds.extend(new google.maps.LatLng(hospital.latitude, hospital.longitude));
        });

        // When embedded in the drilldown modal's iframe pane (?embed=1), the viewport is
        // narrow (~40% width). fitBounds in that case can produce odd off-center views.
        // Use a fixed CONUS-centered view at zoom 4 so the user sees the whole country.
        const isEmbedded = typeof window !== 'undefined' &&
          new URLSearchParams(window.location.search).get('embed') === '1';

        const CONUS_CENTER = { lat: 39.8283, lng: -98.5795 };  // geographic center of CONUS
        const mapCenter = isEmbedded
          ? CONUS_CENTER
          : bounds.isEmpty() ? CONUS_CENTER : bounds.getCenter().toJSON();

        const darkMapStyles: google.maps.MapTypeStyle[] = [
          { "elementType": "geometry", "stylers": [{ "color": "var(--civic-navy-deep)" }] },
          { "elementType": "labels.text.fill", "stylers": [{ "color": "#8ec3b9" }] },
          { "elementType": "labels.text.stroke", "stylers": [{ "color": "#1a3646" }] },
          { "featureType": "water", "elementType": "geometry", "stylers": [{ "color": "#0e1626" }] },
          { "featureType": "administrative", "elementType": "geometry.stroke", "stylers": [{ "color": "#4b6878" }] },
          { "featureType": "road", "elementType": "geometry", "stylers": [{ "color": "var(--civic-navy-deep)" }] },
          { "featureType": "road", "elementType": "geometry.stroke", "stylers": [{ "color": "#1a252f" }] }
        ];

        const ahpMapStyles: google.maps.MapTypeStyle[] = [
          { "elementType": "geometry", "stylers": [{ "color": "#f5f5f5" }] },
          { "elementType": "labels.text.fill", "stylers": [{ "color": "var(--civic-navy)" }] },
          { "elementType": "labels.text.stroke", "stylers": [{ "color": "#ffffff" }] },
          { "featureType": "water", "elementType": "geometry", "stylers": [{ "color": "#c9d7e8" }] },
          { "featureType": "administrative", "elementType": "geometry.stroke", "stylers": [{ "color": "var(--civic-navy)" }, { "weight": 1.2 }] },
          { "featureType": "administrative.province", "elementType": "geometry.stroke", "stylers": [{ "color": "var(--civic-red)" }, { "weight": 0.8 }] },
          { "featureType": "road", "elementType": "geometry", "stylers": [{ "color": "#e0e0e0" }] },
          { "featureType": "road", "elementType": "geometry.stroke", "stylers": [{ "color": "#d5d5d5" }] },
          { "featureType": "poi", "elementType": "geometry", "stylers": [{ "color": "#eeeeee" }] },
          { "featureType": "landscape", "elementType": "geometry", "stylers": [{ "color": "var(--civic-cream)" }] }
        ];

        const mapStyles = brand.key === 'ahp' ? ahpMapStyles : darkMapStyles;

        const googleMap = new google.maps.Map(mapRef.current, {
          // Wheel over the map scrolls the page; ctrl+wheel (or pinch) zooms.
          gestureHandling: 'cooperative',
          center: mapCenter,
          zoom: isEmbedded ? 4 : 5,
          minZoom: 4,
          maxZoom: 18,
          restriction: {
            latLngBounds: { north: 71.5, south: 18.0, west: -180, east: -50 },
            strictBounds: false
          },
          styles: mapStyles
        });

        // Skip fitBounds when embedded — narrow viewport produces off-center views.
        // Embedded users see the whole CONUS at zoom 4 instead.
        if (!isEmbedded) {
          googleMap.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
        }

        google.maps.event.addListenerOnce(googleMap, 'bounds_changed', () => {
          const currentZoom = googleMap.getZoom();
          if (currentZoom !== undefined && currentZoom > 15) {
            googleMap.setZoom(12);
          } else if (currentZoom !== undefined && currentZoom < 4) {
            googleMap.setZoom(5);
          }
        });

        // Create heatmap with ALL valid hospitals
        const maxPrice = Math.max(...validHospitals.map(h => h.gross_charge));
        const minPrice = Math.min(...validHospitals.map(h => h.gross_charge));
        const priceRange = maxPrice - minPrice;

        const heatmapData = validHospitals.map(hospital => {
          const normalizedPrice = priceRange > 0
            ? (hospital.gross_charge - minPrice) / priceRange
            : 0.5;
          const weight = 0.1 + (normalizedPrice * 0.9);
          return {
            location: new google.maps.LatLng(hospital.latitude, hospital.longitude),
            weight: weight * 100
          };
        });

        const heatmap = new google.maps.visualization.HeatmapLayer({
          data: heatmapData,
          map: googleMap,
          radius: 50,
          opacity: 0.7,
          gradient: heatmapGradient
        });

        heatmapRef.current = heatmap;

        // Shared InfoWindow
        infoWindowRef.current = new google.maps.InfoWindow();

        googleMap.addListener('idle', () => {
          const b = googleMap.getBounds();
          if (!b) return;
          const ne = b.getNorthEast(), sw = b.getSouthWest();
          setViewBounds({ n: ne.lat(), e: ne.lng(), s: sw.lat(), w: sw.lng() });
        });
        setMap(googleMap);
        setLoading(false);
        setError(null);
      } catch (err) {
        console.error('Error initializing map:', err);
        setError('Failed to load map. Please check your Google Maps API key.');
        setLoading(false);
      }
    };

    initializeMap();

    return () => {
      if (heatmapRef.current) {
        heatmapRef.current.setMap(null);
      }
      markersRef.current.forEach(marker => marker.setMap(null));
      markersRef.current = [];
      markerMapRef.current.clear();
    };
  }, [hospitals, GOOGLE_MAPS_API_KEY]);

  // --- Effect 2: Markers only (re-runs when slider/toggle/map changes) ---
  useEffect(() => {
    if (!map || validHospitals.length === 0) return;

    // Clear old markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
    markerMapRef.current.clear();

    const infoWindow = infoWindowRef.current;
    if (!infoWindow) return;

    visibleMarkerHospitals.forEach((hospital, index) => {
      const color = getMarkerColor(hospital.gross_charge, avgPrice);

      const marker = new google.maps.Marker({
        position: { lat: hospital.latitude, lng: hospital.longitude },
        map: map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: color,
          fillOpacity: 0.9,
          strokeColor: '#ffffff',
          strokeWeight: 1.5,
          scale: 5,
        },
        title: hospital.hospital_name,
        zIndex: 1000 + index,
      });

      marker.addListener('click', () => {
        infoWindow.setContent(
          buildInfoContent(hospital, color, avgPrice, codeType, procedureCode, hospitals.length)
        );
        infoWindow.open(map, marker);
        setSelectedHospital(hospital.hospital_id);
      });

      markersRef.current.push(marker);
      markerMapRef.current.set(hospital.hospital_id, marker);
    });
  }, [map, visibleMarkerHospitals, avgPrice, codeType, procedureCode, hospitals.length]);

  // --- Hospital click handler (works with or without existing marker) ---
  const handleHospitalClick = useCallback((hospital: HospitalMapData) => {
    if (!map) return;

    const existingMarker = markerMapRef.current.get(hospital.hospital_id);
    const infoWindow = infoWindowRef.current;

    if (existingMarker) {
      map.panTo(existingMarker.getPosition()!);
      map.setZoom(8);
      google.maps.event.trigger(existingMarker, 'click');
      setSelectedHospital(hospital.hospital_id);
    } else if (
      hospital.latitude && hospital.longitude &&
      !isNaN(hospital.latitude) && !isNaN(hospital.longitude) &&
      hospital.latitude !== 0 && hospital.longitude !== 0
    ) {
      // Create a temporary highlighted marker for hospitals not in the visible set
      const color = getMarkerColor(hospital.gross_charge, avgPrice);

      const tempMarker = new google.maps.Marker({
        position: { lat: hospital.latitude, lng: hospital.longitude },
        map: map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: '#fbbf24',
          fillOpacity: 1,
          strokeColor: color,
          strokeWeight: 3,
          scale: 8,
        },
        title: hospital.hospital_name,
        zIndex: 9999,
      });

      map.panTo(tempMarker.getPosition()!);
      map.setZoom(8);

      if (infoWindow) {
        infoWindow.setContent(
          buildInfoContent(hospital, color, avgPrice, codeType, procedureCode, hospitals.length)
        );
        infoWindow.open(map, tempMarker);
      }

      // Remove temp marker when info window closes
      if (infoWindow) {
        google.maps.event.addListenerOnce(infoWindow, 'closeclick', () => {
          tempMarker.setMap(null);
        });
      }

      setSelectedHospital(hospital.hospital_id);
    }
  }, [map, avgPrice, codeType, procedureCode, hospitals.length]);

  // Scroll to top when sort direction changes
  useEffect(() => {
    rankingsParentRef.current?.scrollTo({ top: 0 });
  }, [showExpensive]);

  // --- Virtual list ---
  const virtualizer = useVirtualizer({
    count: sortedHospitals.length,
    getScrollElement: () => rankingsParentRef.current,
    estimateSize: () => 56,
    overscan: 10,
  });

  if (hospitals.length === 0) {
    return null;
  }

  const isAhp = brand.design === 'civic';
  const accentColor = isAhp ? 'var(--civic-navy)' : '#22c55e';
  // Heading ink must flip in dark mode (was hardcoded var(--civic-ink) → dark-on-dark).
  const headingInk = ahpDark ? 'var(--civic-warm)' : 'var(--civic-ink)';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2" style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", color: headingInk } : {}}>
          <MapPin className={`w-5 h-5 ${isAhp ? 'text-civic-navy' : 'text-matrix-green'}`} />
          Hospital Price Heatmap - {codeType} {procedureCode}
          {(intl?.length ?? 0) > 0 && (
            <span className="ml-auto inline-flex rounded-md overflow-hidden border text-[11px] font-sans font-semibold"
                  style={{ borderColor: isAhp ? '#00286833' : '#22c55e55' }}>
              {(['usa','world'] as const).map(sc => (
                <button key={sc} type="button" onClick={() => setScope(sc)}
                  className="px-2.5 py-1 uppercase tracking-wide transition-colors"
                  title={sc === 'usa' ? 'US hospitals only' : `Every country with a mapped price for ${codeType} ${procedureCode}, graded A–F against the US average`}
                  style={scope === sc
                    ? { background: isAhp ? 'var(--civic-navy)' : '#22c55e', color: '#fff' }
                    : { background: 'transparent', color: isAhp ? 'var(--civic-navy)' : '#22c55e' }}>
                  {sc === 'usa' ? 'USA only' : 'International'}
                </button>
              ))}
            </span>
          )}
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-2">
          Every marker is graded on the site's A–F scale against the U.S. average for this code —
          <span className="font-medium" style={{ color: gradeColor('A') }}> A</span> = far below it,
          <span className="font-medium" style={{ color: gradeColor('F') }}> F</span> = far above.
          Click markers for details; zoom in and the marker budget respends on the area you're looking at.
        </p>
        {scope === 'usa' && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[12px] font-sans">
            <button type="button"
              onClick={() => {
                setGeoErr(null);
                if (!navigator.geolocation) { setGeoErr('Location unavailable in this browser.'); return; }
                navigator.geolocation.getCurrentPosition(
                  pos => setUserLoc([pos.coords.latitude, pos.coords.longitude]),
                  () => setGeoErr('Location permission denied — the map stays national.'),
                  { maximumAge: 300000, timeout: 8000 });
              }}
              className="px-3 py-1.5 rounded-full font-semibold tracking-wide"
              style={{ background: 'var(--civic-navy)', color: 'var(--civic-parchment)', border: '1.5px solid var(--civic-gold)' }}>
              ★ {userLoc ? 'Update my location' : 'Find hospitals near me'}
            </button>
            {userLoc && (
              <>
                {[25, 50, 100, 250].map(r => (
                  <button key={r} type="button" onClick={() => setRadiusMi(r)}
                    className="px-2.5 py-1 rounded-full font-semibold"
                    style={radiusMi === r
                      ? { background: 'var(--civic-red)', color: '#fff', border: '1.5px solid var(--civic-red)' }
                      : { background: 'transparent', color: 'var(--civic-navy)', border: '1.5px solid #00286844' }}>
                    {r} mi
                  </button>
                ))}
                <span className="text-muted-foreground">
                  {geoFilteredHospitals.length.toLocaleString()} hospitals within {radiusMi} mi
                </span>
                <button type="button" onClick={() => setUserLoc(null)}
                  className="underline text-muted-foreground hover:text-foreground">clear</button>
              </>
            )}
            {geoErr && <span style={{ color: 'var(--civic-red)' }}>{geoErr}</span>}
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-[2fr,1fr] gap-4">
          {/* Map Section */}
          <div>
            {loading && (
              <div className="flex items-center justify-center h-[600px] bg-secondary/20 rounded-lg">
                <div className="flex flex-col items-center gap-4">
                  <Loader2 className="w-8 h-8 animate-spin text-matrix-green" />
                  <p className="text-muted-foreground">Loading heatmap...</p>
                </div>
              </div>
            )}
            {/* Open-source fallback: Google unavailable (no key / load fail /
                auth failure). Same colored markers, no API key required. */}
            {(error || gmFailed) && (
              <div className="w-full h-[600px] rounded-lg overflow-hidden" style={{ minHeight: '600px' }}>
                <OpenStreetMapFallback
                  key={`${scope}-${userLoc ? userLoc.join(',') + '-' + radiusMi : 'all'}`}
                  scrollWheelZoom={false}
                  legendTitle="Grade vs US natl avg"
                  hospitals={visibleMarkerHospitals.map((h, i) => ({
                    hospital_id: h.hospital_id, hospital_name: h.hospital_name,
                    latitude: h.latitude, longitude: h.longitude, city: h.city, state: h.state,
                    gross_charge: h.gross_charge,
                    // Always-visible A–F badge, same as /map/ — the letter IS the label.
                    grade: avgPrice > 0 ? getGrade(((h.gross_charge - avgPrice) / avgPrice) * 100) : null,
                    rank: i + 1,
                  }))}
                  totalRanked={geoFilteredHospitals.length}
                  avgPrice={avgPrice}
                  dark={ahpDark}
                  colorFor={getMarkerColor}
                  legend={[
                    { color: gradeColor('A'), label: 'A · ≤30% of natl avg' },
                    { color: gradeColor('B'), label: 'B · 30–65%' },
                    { color: gradeColor('C'), label: 'C · 65–95%' },
                    { color: gradeColor('D'), label: 'D · 95–130%' },
                    { color: gradeColor('F'), label: 'F · >130% of natl avg' },
                  ]}
                  onSelect={(id) => setSelectedHospital(id)}
                />
              </div>
            )}
            <div
              ref={mapRef}
              className={`w-full h-[600px] rounded-lg ${loading || error || gmFailed ? 'hidden' : ''}`}
              style={{ minHeight: '600px' }}
            />
          </div>

          {/* Rankings Panel — shown alongside the map (Google or OSM fallback). */}
          {!loading && (
            <div className="bg-secondary/20 rounded-lg flex flex-col h-[600px]">
              {/* Controls Bar */}
              <div className="p-3 border-b border-border/50 space-y-3">
                {/* Toggle: Most Expensive / Cheapest */}
                <div className="flex rounded-lg overflow-hidden border border-border/50">
                  <button
                    onClick={() => setShowExpensive(true)}
                    className="flex-1 text-xs font-semibold py-1.5 px-2 transition-colors"
                    style={showExpensive ? { backgroundColor: accentColor, color: '#fff' } : {}}
                  >
                    Most Expensive
                  </button>
                  <button
                    onClick={() => setShowExpensive(false)}
                    className="flex-1 text-xs font-semibold py-1.5 px-2 transition-colors"
                    style={!showExpensive ? { backgroundColor: accentColor, color: '#fff' } : {}}
                  >
                    Cheapest
                  </button>
                </div>

                {/* Slider */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                    <span>Map pins</span>
                    <span className="font-mono font-semibold">{maxMarkers}</span>
                  </div>
                  <Slider
                    value={[maxMarkers]}
                    onValueChange={(v) => setMaxMarkers(v[0])}
                    min={50}
                    max={Math.min(2000, validHospitals.length)}
                    step={50}
                  />
                </div>

                <p className="text-[11px] text-muted-foreground">
                  Showing {Math.min(maxMarkers, geoFilteredHospitals.length).toLocaleString()} of {geoFilteredHospitals.length.toLocaleString()} on map
                  <span className="opacity-70"> · #1 = {showExpensive ? 'most expensive' : 'cheapest'}</span>
                </p>
              </div>

              {/* Column header */}
              <div className="grid grid-cols-[2rem,1fr,4.5rem,3.5rem] gap-1 px-3 py-2 text-[11px] font-semibold text-muted-foreground border-b border-border/50">
                <span>#</span>
                <span>Hospital</span>
                <span className="text-right">Price</span>
                {quality && <span className="text-right" title="CMS Care Compare star rating and the HCAHPS patient survey, published by Medicare. Not our rating.">CMS</span>}
              </div>
              {quality && (
                <div className="px-3 py-1 text-[9px] leading-snug border-b border-border/40 text-muted-foreground">
                  <span style={{ color: 'var(--civic-gold)' }}>★</span> = CMS Care Compare rating ·
                  <span className="font-mono"> %</span> = share of its own patients who would
                  definitely recommend it (CMS HCAHPS survey). Third-party data — not our rating.
                </div>
              )}

              {/* Virtualized rows */}
              <div
                ref={rankingsParentRef}
                className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
              >
                <div
                  style={{ height: `${virtualizer.getTotalSize()}px`, width: '100%', position: 'relative' }}
                >
                  {virtualizer.getVirtualItems().map((virtualRow) => {
                    const hospital = sortedHospitals[virtualRow.index];
                    const rank = virtualRow.index + 1;
                    const hasMarker = markerHospitalIds.has(hospital.hospital_id);
                    const isSelected = selectedHospital === hospital.hospital_id;

                    return (
                      <div
                        key={`${hospital.hospital_id}-${virtualRow.index}`}
                        data-index={virtualRow.index}
                        ref={virtualizer.measureElement}
                        className={`absolute left-0 w-full grid grid-cols-[2rem,1fr,4.5rem,3.5rem] gap-1 items-center px-3 py-2 cursor-pointer transition-colors hover:bg-secondary/40 border-b border-border/20 ${
                          isSelected ? 'bg-primary/10 border-l-2' : ''
                        } ${!hasMarker ? 'opacity-60' : ''}`}
                        style={{
                          transform: `translateY(${virtualRow.start}px)`,
                          borderLeftColor: isSelected ? accentColor : 'transparent',
                        }}
                        onClick={() => handleHospitalClick(hospital)}
                      >
                        {/* Rank */}
                        <div>
                          <Badge
                            variant={rank === 1 ? 'destructive' : rank <= 3 ? 'default' : 'secondary'}
                            className="text-[10px] px-1.5 py-0"
                          >
                            {rank}
                          </Badge>
                        </div>

                        {/* Hospital info */}
                        <div className="min-w-0">
                          <div className="text-xs font-medium truncate" title={hospital.hospital_name}>
                            {hospital.hospital_name}
                          </div>
                          <div className="text-[10px] text-muted-foreground truncate">
                            {hospital.city}, {hospital.state}
                          </div>
                        </div>

                        {/* Price + action */}
                        <div className="text-right">
                          <div className="font-mono text-xs font-semibold">
                            ${hospital.gross_charge.toLocaleString()}
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-5 text-[9px] px-1 mt-0.5"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (hospital.hospital_id.startsWith('country:')) { navigate('/international'); return; }
                              navigate(`/hospital/${hospital.hospital_id}`);
                            }}
                            title="Open hospital prices"
                          >
                            <DollarSign className="h-3 w-3 mr-0.5" />
                            Prices
                          </Button>
                        </div>

                        {/* CMS quality — third-party, never our own rating */}
                        {quality && (() => {
                          const q = quality.get(hospital.hospital_id);
                          if (!q || (q.cms_stars == null && q.hcahps_recommend == null)) {
                            return <div className="text-right text-[9px] opacity-30">n/a</div>;
                          }
                          return (
                            <div className="text-right leading-tight">
                              {q.cms_stars != null && (
                                <div className="text-[10px]" style={{ color: 'var(--civic-gold)' }}
                                     title={`CMS Care Compare: ${q.cms_stars} of 5 stars`}>
                                  {'\u2605'.repeat(q.cms_stars)}
                                </div>
                              )}
                              {q.hcahps_recommend != null && (
                                <div className="font-mono text-[9px] text-muted-foreground"
                                     title="HCAHPS: share of this hospital's own patients who would definitely recommend it (CMS survey)">
                                  {q.hcahps_recommend}%
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Legend */}
        {!loading && !error && (
          <div className="mt-4 p-4 bg-secondary/20 rounded-lg">
            <h4 className="text-sm font-semibold mb-3" style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", color: headingInk } : {}}>Heatmap Intensity Guide</h4>
            <div className="flex items-center gap-2 mb-3">
              <div
                className="h-6 w-full rounded"
                style={{ background: legendGradient }}
              ></div>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Lower Prices (Better Value)</span>
              <span>Higher Prices (More Expensive)</span>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs font-semibold mb-2">Price Range</p>
                <p className="text-sm">
                  ${Math.min(...hospitals.map(h => h.gross_charge)).toLocaleString()} -
                  ${Math.max(...hospitals.map(h => h.gross_charge)).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold mb-2">Average Price</p>
                <p className="text-sm">${avgPrice.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs font-semibold mb-2">Median Price</p>
                <p className="text-sm">
                  ${(() => {
                    const sorted = [...hospitals].sort((a, b) => a.gross_charge - b.gross_charge);
                    const mid = Math.floor(sorted.length / 2);
                    return sorted.length % 2 === 0
                      ? Math.round((sorted[mid - 1].gross_charge + sorted[mid].gross_charge) / 2).toLocaleString()
                      : sorted[mid].gross_charge.toLocaleString();
                  })()}
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
});
